/**
 * Recently viewed carousel.
 * Reads product ids/handles from localStorage, fetches card markup, inits Swiper.
 */
const STORAGE_KEY = 'furbish-recently-viewed';

function readStoredProducts() {
  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(items) ? items : [];
  } catch (error) {
    return [];
  }
}

function storeProduct(id, handle) {
  if (typeof window.__storeRecentlyViewed === 'function') {
    window.__storeRecentlyViewed(id, handle);
    return;
  }

  const normalizedHandle = String(handle || '')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '');
  const normalizedId = id ? String(id) : '';
  if (!normalizedId && !normalizedHandle) return;

  try {
    const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const list = Array.isArray(items) ? items : [];
    const next = list.filter((item) => {
      if (!item) return false;
      const sameId = normalizedId && String(item.id) === normalizedId;
      const sameHandle = normalizedHandle && item.handle === normalizedHandle;
      return !sameId && !sameHandle;
    });
    next.unshift({ id: normalizedId, handle: normalizedHandle });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next.slice(0, 12)));
  } catch (error) {}
}

function isCurrentProduct(item, currentId, currentHandle) {
  if (!item) return true;
  if (currentId && String(item.id) === String(currentId)) return true;
  if (currentHandle && item.handle === currentHandle) return true;
  return false;
}

function extractSlideHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const slide = doc.querySelector('.recently-viewed__slide');
  if (slide) return slide.outerHTML;

  const card = doc.querySelector('lp-collection-product-card');
  if (!card) return '';

  const wrap = document.createElement('div');
  wrap.className = 'swiper-slide recently-viewed__slide';
  wrap.appendChild(card);
  return wrap.outerHTML;
}

export class RecentlyViewedSection extends HTMLElement {
  connectedCallback() {
    this.swiper = null;
    this._onResize = null;
    this.wrapper = this.querySelector('[data-recently-viewed-wrapper]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dotsWrap = this.querySelector('[data-dots]');
    this.currentId = this.dataset.currentProductId || '';
    this.currentHandle = this.dataset.currentProductHandle || '';
    this.limit = parseInt(this.dataset.limit, 10) || 8;
    this.designMode = this.dataset.designMode === 'true';

    if (this.designMode) {
      this.hidden = false;
      this.updateStructuredData();
      this.initWhenReady();
      return;
    }

    this.loadProducts();
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this.destroySwiper();
  }

  async loadProducts() {
    storeProduct(this.currentId, this.currentHandle);

    const items = readStoredProducts()
      .filter((item) => item && item.handle && !isCurrentProduct(item, this.currentId, this.currentHandle))
      .slice(0, this.limit);

    if (!items.length || !this.wrapper) {
      this.hideSection();
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const markup = await Promise.all(
      items.map((item) =>
        fetch(`${root}products/${encodeURIComponent(item.handle)}?view=recently-viewed-card`)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => (html ? extractSlideHtml(html) : ''))
          .catch(() => '')
      )
    );

    const html = markup.filter(Boolean).join('');
    if (!html) {
      this.hideSection();
      return;
    }

    this.wrapper.innerHTML = html;
    this.hidden = false;
    this.updateStructuredData();
    this.initWhenReady();
  }

  hideSection() {
    this.hidden = true;
    this.destroySwiper();
  }

  updateStructuredData() {
    const script = this.querySelector('[data-recently-viewed-schema]');
    if (!script) return;

    const heading = this.querySelector('.recently-viewed__heading');
    const seen = new Set();
    const itemListElement = [];

    this.querySelectorAll('.recently-viewed__slide').forEach((slide) => {
      const name =
        slide.dataset.productName ||
        (slide.querySelector('.lp-collection-card-v2__title')?.textContent || '').trim();
      const url = slide.dataset.productUrl || slide.querySelector('a[href*="/products/"]')?.href || '';
      if (!url || seen.has(url)) return;
      seen.add(url);

      const image = slide.dataset.productImage || slide.querySelector('img')?.currentSrc || slide.querySelector('img')?.src || '';
      const description = slide.dataset.productDescription || name || url;
      const product = {
        '@type': 'Product',
        name: name || url,
        url,
        description,
      };
      if (image) product.image = image;

      itemListElement.push({
        '@type': 'ListItem',
        position: itemListElement.length + 1,
        item: product,
      });
    });

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: heading ? heading.textContent.trim() : 'Recently viewed',
      numberOfItems: itemListElement.length,
      itemListElement,
    });
  }

  initWhenReady() {
    if (window.Swiper) {
      this.initSwiper();
      return;
    }

    const waitForSwiper = () => {
      if (window.Swiper) this.initSwiper();
      else requestAnimationFrame(waitForSwiper);
    };
    requestAnimationFrame(waitForSwiper);
  }

  initSwiper() {
    const swiperEl = this.querySelector('.recently-viewed__swiper');
    if (!swiperEl || swiperEl.classList.contains('swiper-initialized')) return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (!slides.length) {
      this.hideSection();
      return;
    }

    const paginationLabel = this.dataset.paginationLabel || 'Go to page [index]';
    const paginationConfig = this.dotsWrap
      ? {
          el: this.dotsWrap,
          clickable: true,
          bulletClass: 'recently-viewed__dot',
          bulletActiveClass: 'recently-viewed__dot--active',
          renderBullet: (index, className) => {
            const label = paginationLabel.replace(/\[index\]/g, String(index + 1));
            return `<button type="button" class="${className}" aria-label="${label}"></button>`;
          },
        }
      : false;

    this.swiper = new window.Swiper(swiperEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 16,
      watchOverflow: true,
      grabCursor: true,
      centeredSlides: false,
      a11y: {
        enabled: true,
        prevSlideMessage: this.prevBtn?.getAttribute('aria-label') || 'Previous products',
        nextSlideMessage: this.nextBtn?.getAttribute('aria-label') || 'Next products',
        paginationBulletMessage: paginationLabel.replace(/\[index\]/g, '{{index}}'),
      },
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      pagination: paginationConfig,
      on: {
        init: () =>
          requestAnimationFrame(() => {
            this.syncControls();
            this.syncNavA11y();
          }),
        resize: () => {
          this.syncControls();
          this.syncNavA11y();
        },
        breakpoint: () => {
          this.syncControls();
          this.syncNavA11y();
        },
        slideChange: () => this.syncNavA11y(),
      },
    });

    this._onResize = () => this.syncControls();
    window.addEventListener('resize', this._onResize);
  }

  syncControls() {
    if (!this.swiper) return;
    // Cards are a fixed width, so how many fit depends on the screen; Swiper locks
    // the track (watchOverflow) exactly when they all fit.
    this.swiper.update();
    const needsNav = this.swiper.slides.length > 1 && !this.swiper.isLocked;
    this.setControlsHidden(!needsNav);

    if (this.swiper.pagination && needsNav) {
      this.swiper.pagination.render();
      this.swiper.pagination.update();
    }
  }

  setControlsHidden(hidden) {
    if (this.dotsWrap) this.dotsWrap.hidden = hidden;
    if (this.prevBtn) this.prevBtn.hidden = hidden;
    if (this.nextBtn) this.nextBtn.hidden = hidden;
    this.syncNavA11y();
  }

  syncNavA11y() {
    const buttons = [this.prevBtn, this.nextBtn];
    buttons.forEach((button) => {
      if (!button) return;
      const disabled = Boolean(button.hidden) || button.classList.contains('swiper-button-disabled');
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.tabIndex = disabled ? -1 : 0;
    });

    if (!this.dotsWrap || this.dotsWrap.hidden) return;
    this.dotsWrap.querySelectorAll('.recently-viewed__dot').forEach((dot) => {
      const current = dot.classList.contains('recently-viewed__dot--active');
      if (current) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  destroySwiper() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}
